import { useEffect } from 'react'
import styled, { keyframes } from 'styled-components'
import { useNavigate } from 'react-router-dom'

const slideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`

const slideOut = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
`

const ToastContainer = styled.div<{ closing?: boolean }>`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: 380px;
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  animation: ${({ closing }) => (closing ? slideOut : slideIn)} 0.2s ease-out forwards;
  overflow: hidden;
`

const ToastHeader = styled.div`
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  padding: 0.75rem 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const ToastTitle = styled.strong`
  font-size: ${({ theme }) => theme.fontSizes.sm};
`

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0;
  line-height: 1;

  &:hover {
    opacity: 0.8;
  }
`

const ToastSummary = styled.div`
  padding: 0.75rem 1rem;
  background: ${({ theme }) => theme.colors.secondary};
  display: flex;
  justify-content: space-between;
  font-size: ${({ theme }) => theme.fontSizes.sm};
`

const TotalCapacity = styled.span<{ color: string }>`
  font-weight: 700;
  color: ${({ color }) => color};
`

const ToastProjects = styled.div`
  padding: 0.75rem 1rem;
  max-height: 350px;
  overflow-y: auto;
`

const ProjectItem = styled.div<{ type: string }>`
  padding: 0.75rem;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: 0.5rem;
  background: ${({ type }) => type === 'proyecto' ? '#e8f5e9' : '#e3f2fd'};
  border-left: 3px solid ${({ type }) => type === 'proyecto' ? '#4CAF50' : '#2196F3'};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    transform: translateX(4px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  &:last-child {
    margin-bottom: 0;
  }
`

const ProjectHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.5rem;
`

const ProjectName = styled.span`
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  flex: 1;
  margin-right: 8px;
`

const ProjectType = styled.span<{ type: string }>`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  padding: 2px 6px;
  border-radius: 4px;
  background: ${({ type }) => type === 'proyecto' ? '#4CAF50' : '#2196F3'};
  color: white;
  font-weight: 500;
`

const ProjectLoad = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const LoadLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textMuted};
`

const LoadValue = styled.span<{ color: string }>`
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ color }) => color};
`

const ProgressBar = styled.div`
  height: 6px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 3px;
  overflow: hidden;
`

const ProgressFill = styled.div<{ width: number; color: string }>`
  height: 100%;
  width: ${({ width }) => Math.min(width, 100)}%;
  background: ${({ color }) => color};
  border-radius: 3px;
  transition: width 0.3s ease;
`

const ProjectDates = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 8px;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textMuted};
`

const DateItem = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
`

const EmptyMessage = styled.div`
  color: ${({ theme }) => theme.colors.textMuted};
  text-align: center;
  padding: 1rem;
  font-size: ${({ theme }) => theme.fontSizes.sm};
`

interface PersonBreakdownToastProps {
    person: {
        display_name: string
        total_capacity: number
        future_max_capacity?: number
        opportunities?: Array<{
            id: number
            name: string
            capacity_pct: number
            type?: string
            oferta_fecha_inicio?: string
            oferta_fecha_entrega?: string
            proyecto_fecha_inicio?: string
            proyecto_fecha_fin?: string
            effective_start_date?: string | null
            effective_end_date?: string | null
        }>
        projects?: Array<{
            id: number
            name: string
            capacity_pct: number
            type?: string
            oferta_fecha_inicio?: string
            oferta_fecha_entrega?: string
            proyecto_fecha_inicio?: string
            proyecto_fecha_fin?: string
            effective_start_date?: string | null
            effective_end_date?: string | null
        }>
    }
    onClose: () => void
    onViewDetails?: (opportunityId: number) => void
}

const getLoadColor = (capacity: number): string => {
    if (capacity === 0) return '#9E9E9E'
    if (capacity < 50) return '#4CAF50'
    if (capacity <= 80) return '#FF9800'
    return '#F44336'
}

const formatDate = (dateStr?: string): string => {
    if (!dateStr) return '-'
    try {
        return new Date(dateStr).toLocaleDateString('es-ES', {
            day: '2-digit',
            month: 'short'
        })
    } catch {
        return '-'
    }
}

export const PersonBreakdownToast: React.FC<PersonBreakdownToastProps> = ({
    person,
    onClose,
    onViewDetails,
}) => {
    const navigate = useNavigate()
    const projects = person.opportunities || person.projects || []
    const totalCapacity = person.total_capacity || 0
    const futureMaxCapacity = person.future_max_capacity ?? totalCapacity

    useEffect(() => {
        const timer = setTimeout(() => {
            onClose()
        }, 15000) // 15 seconds

        return () => clearTimeout(timer)
    }, [onClose])

    const handleProjectClick = (projectId: number) => {
        if (onViewDetails) {
            onViewDetails(projectId)
        } else {
            navigate(`/oportunidades`, { state: { openOpportunityId: projectId } })
        }
    }

    return (
        <ToastContainer>
            <ToastHeader>
                <ToastTitle>👤 {person.display_name}</ToastTitle>
                <CloseButton onClick={onClose}>&times;</CloseButton>
            </ToastHeader>

            <ToastSummary>
                <span>Carga Hoy:</span>
                <TotalCapacity color={getLoadColor(totalCapacity)}>
                    {totalCapacity}%
                    {totalCapacity > 100 && ' ⚠️ Sobrecargado'}
                </TotalCapacity>
            </ToastSummary>

            {futureMaxCapacity > 100 && futureMaxCapacity > totalCapacity && (
                <div style={{
                    padding: '0.5rem 1rem',
                    background: '#FFF3E0',
                    borderBottom: '1px solid #FFCC80',
                    fontSize: '0.8rem',
                    color: '#E65100',
                    fontWeight: 600,
                }}>
                    ⚠️ Alerta: carga futura máxima del {futureMaxCapacity}% (sobrecarga prevista)
                </div>
            )}

            <ToastProjects>
                {projects.length === 0 ? (
                    <EmptyMessage>
                        📭 Sin asignaciones actuales
                    </EmptyMessage>
                ) : (
                    projects.map((project) => {
                        const pct = project.capacity_pct || 0
                        const color = getLoadColor(pct)
                        const type = project.type || 'oferta'

                        // Prefer the backend-computed effective dates so deferred-start roles
                        // (start_availability > Inmediato) are surfaced correctly in the tooltip.
                        const startDate = project.effective_start_date
                            || (type === 'proyecto' ? project.proyecto_fecha_inicio : project.oferta_fecha_inicio)
                        const endDate = project.effective_end_date
                            || (type === 'proyecto' ? project.proyecto_fecha_fin : project.oferta_fecha_entrega)

                        return (
                            <ProjectItem
                                key={project.id}
                                type={type}
                                onClick={() => handleProjectClick(project.id)}
                            >
                                <ProjectHeader>
                                    <ProjectName>{project.name}</ProjectName>
                                    <ProjectType type={type}>
                                        {type === 'proyecto' ? '🚀 Proyecto' : '📋 Oferta'}
                                    </ProjectType>
                                </ProjectHeader>

                                <ProjectLoad>
                                    <LoadLabel>Dedicación:</LoadLabel>
                                    <LoadValue color={color}>{pct}%</LoadValue>
                                </ProjectLoad>

                                <ProgressBar>
                                    <ProgressFill width={pct} color={color} />
                                </ProgressBar>

                                {(startDate || endDate) && (
                                    <ProjectDates>
                                        <DateItem>📅 {formatDate(startDate)}</DateItem>
                                        <DateItem>→</DateItem>
                                        <DateItem>{formatDate(endDate)}</DateItem>
                                    </ProjectDates>
                                )}
                            </ProjectItem>
                        )
                    })
                )}
            </ToastProjects>
        </ToastContainer>
    )
}
